import TextField from '@mui/material/TextField';
import { useEffect, useRef, useState } from 'react';
import _ from '@lodash';
import InputAdornment from '@mui/material/InputAdornment';
import { Controller, useFormContext } from 'react-hook-form';
import AuraFreeSoloControlledAutocomplete from 'app/main/apps/common/AuraFreeSoloControlledAutocomplete';
import FormHelperText from '@mui/material/FormHelperText';
import { getFile } from 'app/main/apps/e-commerce/store/filesSlice';
import { calculateMargin, calculateUnitPrice, calculateWeight, isVasTeam, preventNegativeValues } from 'app/main/apps/common/AuraFunctions';
import { useDispatch, useSelector } from 'react-redux';
import { Typography } from '@mui/material';
import { getOrderItemDiscount, getDefaultWeight } from '../../store/orderSlice';
import { convertToQuoteShippingProfile, getCustomerMargin } from '../../store/rfqSlice';
import { resolveFileSrc } from 'app/main/apps/common/fileSource';

function CustomProductFormContent(props) {
    const dispatch = useDispatch();
    const methods = useFormContext();
    const [weightCalculation, setWeightCalculation] = useState(false)
    const updateWeight = useRef(false)
    const [weightAdjustment, setWeightAdjustment] = useState('')
    const { control, formState, watch, setValue, getValues, trigger, reset } = methods;
    const { errors, isDirty } = formState;
    const productColors = useSelector(({ eCommerceApp }) => eCommerceApp.products.productColors);
    const productTypes = useSelector(({ eCommerceApp }) => eCommerceApp.products.productTypes);
    const productSubTypes = useSelector(({ eCommerceApp }) => eCommerceApp.products.productSubTypes);
    const productPolishes = useSelector(({ eCommerceApp }) => eCommerceApp.products.productPolishes);
    const currentUser = useSelector(({ user }) => user);
    const isCustomer = currentUser && currentUser.role === "customer";
    const stoneColor = watch('stone_color');
    const type = watch('type');
    const length = watch('length');
    const width = watch('width');
    const height = watch('height');
    const polish = watch('polish');
    const fob = watch('unit_cost');
    const unitPriceAmt = watch('unit_price');
    const weight = watch('weight');
    const quantity = watch('order_quantity');
    const margin = watch('margin')
    const shipping = watch('shipping')
    const discount = watch('discount')
    const tariff = watch('tariff')
    const squareFeet = watch('square_feet');
    const pricePerSquareFeet = watch('price_per_square_feet');
    const { mode, isReadOnly: originalIsReadOnly } = props;
    const showDiscount = !isVasTeam(currentUser.role) && (props?.stage !== "rfq"  || (props?.stage === "rfq" && props?.orderType === "convert-to-quote")) && !isCustomer && !props.hideAdditionalFields;
    const isReadOnly = props.isInventoryProduct || originalIsReadOnly;

    const getPolish = () => {
        if (polish) {
            if (typeof polish === 'object') {
                return polish.inputValue || polish.name
            }
            return polish
        }
        return ''
    }

    useEffect(() => {
        if (props.isManage || isCustomer) {
            if (length > 0 && width > 0 && height > 0 && stoneColor && type?.name) {
                const stoneColorName = stoneColor?.inputValue ?? stoneColor.name
                const typeName = type?.inputValue ?? type.name
                setValue('name', `${stoneColorName} ${typeName} ${length}x${width}x${height} ${getPolish()} `, { shouldValidate: true });
                setValue(
                    'description',
                    `${stoneColorName} ${typeName} ${length}x${width}x${height} ${getPolish()} `,
                    { shouldValidate: true }
                );
            }
        }
    }, [length, width, height, polish, stoneColor, type, setValue, props.isManage]);

    useEffect(() => {
        const typeName = type?.inputValue ?? type?.name
        if (['Bevel', 'Slant'].includes(typeName)) {
            const categoryName = `${typeName?.toLowerCase()}_weight_adjustment`
            dispatch(getDefaultWeight(categoryName)).then((action) => {
                const responseName = categoryName.replace(/_/g, ' ');
                setWeightAdjustment(action.payload[responseName])
            })
        } else {
            setWeightAdjustment('')
        }
    }, [type])

    useEffect(() => {
        // Don't calculate and reset unit price (Until form is untouched) in the edit_custom_product or cloned product if unit price is 0.
        // Edit edit_custom_product Popup or cloned product: Do not update the unit price until any one of fob, tariff, shipping, discount, weight, quantityis tocuhed
        if((mode === 'edit_custom_product' || (mode === 'custom_product' && props.cloneProduct)) && !isDirty) return;

        // Skip the unit_price calculation for Edit inventory Product
        if(!props.isCustomProduct && mode === 'edit_custom_product') return;

        const tempPrice = calculateUnitPrice(weight, fob, tariff, shipping, margin);
        const unitPrice = tempPrice > 0 ? tempPrice : '';
        setValue('unit_price', unitPrice, {shouldValidate: isDirty});
    }, [fob, tariff, shipping, discount, weight, quantity])

    useEffect(() => {
        return () => {
          reset()
        };
      }, []);

    useEffect(() => {
        if (length && height && width) {
            const baseWeight = calculateWeight(true, length, width, height);
            const calWeight = _.round(weightAdjustment?.length > 0
                ? baseWeight * (parseInt(weightAdjustment, 10) / 100)
                : baseWeight);
            if (mode === 'edit_custom_product') {
                setWeightCalculation(true)
            } else if(mode === 'custom_product' && props.cloneProduct) {
                // For cloned product, don't update the weight the first time
                if(updateWeight.current === true) {
                    setValue('weight', calWeight)
                }
                updateWeight.current = true;
            } else {
                setValue('weight', calWeight)
            }

            if (weightCalculation) {
                setValue('weight', calWeight)
            }
        }
    }, [length, height, width, mode, weightAdjustment])

    useEffect(() => {
        // Update square_feet when length, width or height changes
        if (length && height && width) {
            // Don't calculate it when poup is open and form is untouched
            if((mode === 'edit_custom_product' || (mode === 'custom_product' && props.cloneProduct)) && !isDirty) return;

            // Arrange in Descending order
            const dimmensions = [Number(length), Number(width), Number(height)].sort((a, b) => b - a);
            // (Multiplication of max two dimension)/144
            const sqFeetTemp = (dimmensions[0] * dimmensions[1]) / 144;
            setValue('square_feet', sqFeetTemp.toFixed(2));
            if(Number(pricePerSquareFeet)) {
                const fobTemp = Number(pricePerSquareFeet) * sqFeetTemp.toFixed(2);
                if(props.stage === 'quote' || props.orderType === 'convert-to-quote') {
                    // Added shouldValidate to remove Required field error after setting unit_cost here
                    setValue('unit_cost', fobTemp.toFixed(2), {shouldValidate: true});
                } else {
                    setValue('unit_cost', fobTemp.toFixed(2));
                }
            }
        }
    }, [length, height, width])

    useEffect(() => {
        // For cloneProduct we dont need to update tariff, margin, shipping
        if (mode !== 'edit_custom_product' && !props.customerId && !props.cloneProduct) {
            dispatch(convertToQuoteShippingProfile({ params: { category: "default_shipping_profile" } })).then(({ payload }) => {
                setValue('tariff', payload.tariff)
                setValue('margin', payload.margin)
                setValue('shipping', payload.shipping)
            })
        }
        // For cloneProduct we dont need to update tariff, margin, shipping and discount value
        if ((mode !== 'edit_custom_product' && props.customerId && !props.cloneProduct) || isCustomer) {
            dispatch(getCustomerMargin({
                customerId: props.customerId,
                orderType: props.formOrderType?.name || 'Regular',
                includeShipping: props.formOrderData?.include_shipping_cost || false,
                orderId: props.formOrderData?.id
            })).then(({ payload }) => {
                setValue('tariff', payload.tariff || 0)
                setValue('margin', payload.margin)
                setValue('shipping', payload.shipping || 0)
                if (payload?.surcharge != null) setValue('default_sur_charge_percentage', payload.surcharge)
            })
            const customerId = isCustomer ? currentUser?.customer?.id : props.customerId
            if(showDiscount === true) {
                dispatch(getOrderItemDiscount({ cid: customerId, stage: props.stage })).then(
                    (action) => {
                        const discountValue =
                            action.payload && action.payload.discount ? action.payload.discount : '';
                        setValue('discount', `${discountValue}`);
                    }
                );
            }
        }
    }, [mode, dispatch])


    function onStoneDataUpdate(event, value) {
        if (value && value.id && value.attachment_id) {
            dispatch(getFile(value.attachment_id)).then((response) => {
                const responseData = response ? response.payload : null;
                if (responseData) {
                    const stoneColorData = { ...value, attachment: responseData.data.file };
                    setValue('stone_color', stoneColorData);
                }
            });
        }
    }

    function stoneValidate() {
        return !!(stoneColor && !stoneColor.name && stoneColor.attachment && stoneColor.attachment.id);
    }

    const handlePercentageValue = (event, field) => {
        const { value } = event.target;

        // Allow empty values, and ensure the value is a valid number or decimal between 0 and 99.99
        if (value === '' || (/^(\d{1,2}(\.\d{0,2})?)$/.test(value) && Number(value) <= 99.99)) {
            field.onChange(value);
        }
    };

    const handleUnitPriceChange = (event, field) => {
        const { value } = event.target;
        let marVal = 0;
        if(value && value > 0) {
            marVal = calculateMargin(weight, fob, tariff, shipping, value);
        }
        setValue('margin', marVal, {shouldValidate: true});
        trigger("margin")
    };

    const handleMarginChange = (event, field) => {
        let { value } = event.target;
        value = value < 0 ? value = '' : value
        // Allow empty values, and ensure the value is a valid number or decimal between 0 and 99.99
        if (value === '' || (/^(\d{1,2}(\.\d{0,2})?)$/.test(value) && Number(value) <= 99.99)) {
            field.onChange(value);
            const price = calculateUnitPrice(weight, fob, tariff, shipping, value);
            setValue('unit_price', price, {shouldValidate: true});
        }
        trigger("unit_price");
    };

    const handleSquareFeetChange = (event, field) => {
        // Update unit_cost on square feet change
        const squareFeetVal = parseFloat(event.target.value);
        if (!Number.isNaN(squareFeetVal)) {
            // Calculation - Fob = sq.ft * Price per square feet
            const fobCalc = pricePerSquareFeet * squareFeetVal;
            if(props.stage === 'quote' || props.orderType === 'convert-to-quote') {
                // Added shouldValidate to remove Required field error after setting unit_cost here
                setValue('unit_cost', fobCalc.toFixed(2), {shouldValidate: true});
            } else {
                setValue('unit_cost', fobCalc.toFixed(2));
            }
        }
    };
    
    const handlePricePerSquareFeetChange = (event, field) => {
        // Update unit_cost on Price per square feet change
        const pricePerSqFt = parseFloat(event.target.value);
        if (!Number.isNaN(pricePerSqFt)) {
            // Calculation - Fob = sq.ft * Price per square feet
            const fobCalc = pricePerSqFt * squareFeet;
            if(props.stage === 'quote' || props.orderType === 'convert-to-quote') {
                // Added shouldValidate to remove Required field error after setting unit_cost here
                setValue('unit_cost', fobCalc.toFixed(2), {shouldValidate: true});
            } else {
                setValue('unit_cost', fobCalc.toFixed(2));
            }
        }
    };

    const handleUnitCostChange = (event, field) => {
        // Update price_per_square_feet on FOB change
        const fobValue = parseFloat(event.target.value);
        if (!Number.isNaN(fobValue) && squareFeet > 0) {
            // Calculation -  Price per square feet = Fob/sq.ft
            const pricePerSqFeetTemp = fobValue / squareFeet;
            setValue('price_per_square_feet', pricePerSqFeetTemp.toFixed(2));
        }
    };

    
    useEffect(() => {
        const handleWheel = (e) => {
          if (e.target.type === "number") {
            e.target.blur();
          }
        };

        document.addEventListener("wheel", handleWheel, { passive: true });

        return () => {
          document.removeEventListener("wheel", handleWheel);
        };
    }, []);
    
    return (
        <div>
            <div className="flex flex-col md:flex-row w-full">
                <div className="flex w-full">
                    <div className="flex flex-col w-full">
                        <div className="flex flex-col md:flex-row w-full">
                            <div className="flex w-full md:w-4/7">
                                <Controller
                                    name="name"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <div className="flex gap-10 mt-8 mb-16">
                                            <Typography variant="body1" className="font-bold">
                                                Name
                                            </Typography>
                                            <Typography variant="body1" color={fieldState.error ? 'error' : 'textSecondary'}>
                                                {fieldState.error ? fieldState.error.message : field.value}
                                            </Typography>
                                        </div>
                                    )}
                                />
                            </div>

                            <div className="flex w-full md:w-4/7 md:ml-2">
                                <Controller
                                    name="description"
                                    control={control}
                                    render={({ field }) => (
                                        <div className="flex gap-10 mt-8 mb-16">
                                            <Typography variant="body1" className="font-bold">
                                                Description
                                            </Typography>
                                            <Typography variant="body1" color="textSecondary">
                                                {field.value}
                                            </Typography>
                                        </div>
                                    )}
                                />
                            </div>
                        </div>

                        {/* First Row */}
                            <div className="flex flex-col md:flex-row w-full">
                                {/* Custom Product - Stone Color */}
                                <div className="flex w-full md:w-1/4">
                                    <AuraFreeSoloControlledAutocomplete
                                        control={control}
                                        controlName="stone_color"
                                        inputValues={productColors}
                                        labelName="Stone Color *"
                                        readOnly={isReadOnly}
                                        onUpdate={onStoneDataUpdate}
                                        textClassName="mt-8 mb-0"
                                    />
                                    {stoneValidate() && <FormHelperText error>Select stone color</FormHelperText>}
                                </div>
                                {/* Custom Product - Length */}
                                <div className="flex w-full md:w-1/4 md:ml-16">
                                    <Controller
                                        name="length"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                className="mt-8 mb-16"
                                                id="length"
                                                label="Length"
                                                variant="outlined"
                                                fullWidth
                                                size="small"
                                                type="number"
                                                onKeyDown={preventNegativeValues}
                                                InputProps={{
                                                    readOnly: isReadOnly,
                                                }}
                                                required={(props.isManage && !isReadOnly) || isCustomer}
                                                error={!!errors.length}
                                                helperText={errors?.length?.message}
                                            />
                                        )}
                                    />
                                </div>
                                {/* Custom Product - Width */}
                                <div className="flex w-full md:w-1/4 md:ml-16">
                                    <Controller
                                        name="width"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                className="mt-8 mb-16"
                                                id="width"
                                                label="Width"
                                                variant="outlined"
                                                type="number"
                                                onKeyDown={preventNegativeValues}
                                                fullWidth
                                                size="small"
                                                required={(props.isManage && !isReadOnly) || isCustomer}
                                                error={!!errors.width}
                                                helperText={errors?.width?.message}
                                                InputProps={{
                                                    readOnly: isReadOnly,
                                                }}
                                            />
                                        )}
                                    />
                                </div>
                                {/* Custom Product - Height */}
                                <div className="flex w-full md:w-1/4 md:ml-16">
                                    <Controller
                                        name="height"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                className="mt-8 mb-16"
                                                id="height"
                                                label="Height"
                                                variant="outlined"
                                                type="number"
                                                onKeyDown={preventNegativeValues}
                                                fullWidth
                                                size="small"
                                                InputProps={{
                                                    readOnly: isReadOnly,
                                                }}
                                                required={(props.isManage && !isReadOnly) || isCustomer}
                                                error={!!errors.height}
                                                helperText={errors?.height?.message}
                                            />
                                        )}
                                    />
                                </div>
                            </div>

                        {/* Second Row */}
                        <div className="flex flex-col md:flex-row w-full gap-16">
                                    {/* Custom Product - Type */}
                                    <div className="flex w-full md:w-1/4">
                                        <AuraFreeSoloControlledAutocomplete
                                            control={control}
                                            controlName="type"
                                            inputValues={productTypes || []}
                                            labelName="Type *"
                                            readOnly={isReadOnly}
                                            onUpdate={() => {
                                                setValue('sub_type', '')
                                                setValue('polish', '')
                                            }}
                                        />
                                    </div>
                                    {/* Custom Product - Sub Type */}
                                    <div className="flex w-full md:w-1/4">
                                        <AuraFreeSoloControlledAutocomplete
                                            control={control}
                                            controlName="sub_type"
                                            inputValues={productSubTypes || []}
                                            labelName="Sub Type"
                                            readOnly={isReadOnly}
                                        />
                                    </div>
                                    {/* Custom Product - Polish */}
                                    <div className="flex w-full md:w-1/4">
                                        <AuraFreeSoloControlledAutocomplete
                                            control={control}
                                            controlName="polish"
                                            inputValues={productPolishes || []}
                                            labelName="Polish *"
                                            readOnly={isReadOnly}
                                            textClassName="mt-8 mb-0"
                                        />
                                    </div>
                                    {/* Custom Product - Order Quantity */}
                                    <div className="flex w-full md:w-1/4">
                                        <Controller
                                            name="order_quantity"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    className="mt-8 mb-16"
                                                    id="order_quantity"
                                                    error={!!errors.order_quantity}
                                                    required={(props.isManage && !isReadOnly) || isCustomer}
                                                    helperText={errors?.order_quantity?.message}
                                                    label="Order Quantity"
                                                    type="number"
                                                    onKeyDown={preventNegativeValues}
                                                    variant="outlined"
                                                    fullWidth
                                                    size="small"
                                                     InputProps={{	
                                                        readOnly: originalIsReadOnly ,	
                                                    }}
                                                />
                                            )}
                                        />
                                    </div>
                           
                        </div>

                        {/* Third Row */}
                        <div className="flex flex-col md:flex-row w-full gap-16">
                            {/* Reference No */}
                            <div className="flex w-full">
                                <Controller
                                    name="customer_provider_number"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            className="mt-8 mb-16"
                                            id="customer_provider_number"
                                            label="Reference No"
                                            variant="outlined"
                                            fullWidth
                                            size="small"
                                            InputProps={{
                                                readOnly: originalIsReadOnly,
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {/* Decedent */}
                            <div className="flex w-full">
                                <Controller
                                    name="decedent"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            className="mt-8 mb-16"
                                            id="decedent"
                                            label="Decedent"
                                            variant="outlined"
                                            fullWidth
                                            size="small"
                                            InputProps={{
                                                readOnly: originalIsReadOnly,
                                            }}
                                        />
                                    )}
                                />
                            </div>
                            {/* Custom Product - Weight */}
                            {!isCustomer && <>
                                <div className="flex w-full">
                                    <Controller
                                        name="weight"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                className="mt-8 mb-16"
                                                id="weight"
                                                error={!!errors.weight}
                                                helperText={errors?.weight?.message}
                                                label="Weight"
                                                variant="outlined"
                                                fullWidth
                                                size="small"
                                                type="number"
                                                onKeyDown={preventNegativeValues}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="start">lbs</InputAdornment>
                                                    ),
                                                    readOnly: isReadOnly,

                                                }}
                                                required
                                            />
                                        )}
                                    />
                                </div>
                                {/* Custom Product - Square Feet */}
                                {!props.hideAdditionalFields && (<>
                                    <div className="flex w-full">
                                        <Controller
                                            name="square_feet"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    className="mt-8 mb-16"
                                                    id="square_feet"
                                                    label="Square Feet"
                                                    variant="outlined"
                                                    fullWidth
                                                    size="small"
                                                    type="number"
                                                    onKeyDown={preventNegativeValues}
                                                    onChange={(event) => {
                                                        field.onChange(event.target.value);
                                                        handleSquareFeetChange(event, field)
                                                    }}
                                                    InputProps={{
                                                        readOnly: isReadOnly,
                                                    }}
                                                />
                                            )}
                                        />
                                    </div>
                                </>)}
                            </>}
                        </div>

                        {/* Fourth Row */}
                        {!props.hideAdditionalFields && (
                            <div className="flex flex-col md:flex-row w-full gap-16">
                                {/* Custom Product Section - Price per Square Feet */}
                                <div className="flex w-full md:w-1/4">
                                    <Controller
                                        name="price_per_square_feet"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                className="mt-8 mb-16"
                                                id="price_per_square_feet"
                                                label="Price per Square Feet"
                                                variant="outlined"
                                                fullWidth
                                                size="small"
                                                type="number"
                                                onKeyDown={preventNegativeValues}
                                                onChange={(event) => {
                                                    field.onChange(event.target.value);
                                                    handlePricePerSquareFeetChange(event, field)
                                                }}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="start">$</InputAdornment>
                                                    ),
                                                    readOnly: isReadOnly,
                                                }}
                                            />
                                        )}
                                    />
                                </div>
                                {/* Custom Product Section - FOB / Unit Cost */}
                                <div className="flex w-full md:w-1/4">
                                    <Controller
                                        name="unit_cost"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                className="mt-8 mb-16"
                                                id="unit_cost"
                                                label="FOB"
                                                variant="outlined"
                                                fullWidth
                                                size="small"
                                                type="number"
                                                onKeyDown={preventNegativeValues}
                                                onChange={(e) => {
                                                    field.onChange(e.target.value);
                                                    handleUnitCostChange(e, field);
                                                }}
                                                error={!!errors.unit_cost}
                                                helperText={errors?.unit_cost?.message}
                                                required={props.stage === 'quote' || props.orderType === 'convert-to-quote'}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="start">$</InputAdornment>
                                                    ),
                                                    readOnly: isReadOnly,
                                                }}
                                            />
                                        )}
                                    />
                                </div>
                                {/* Custom Product Section - Tariff */}
                                <div className="flex w-full md:w-1/4">
                                    <Controller
                                        name="tariff"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                className="mt-8 mb-16"
                                                id="tariff"
                                                label="Tariff"
                                                variant="outlined"
                                                fullWidth
                                                size="small"
                                                type="number"
                                                onKeyDown={preventNegativeValues}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="start">%</InputAdornment>
                                                    ),
                                                    readOnly: isReadOnly,

                                                }}
                                            />
                                        )}
                                    />
                                </div>
                                {/* Custom Product Section - Shipping */}
                                <div className="flex w-full md:w-1/4">
                                    <Controller
                                        name="shipping"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                className="mt-8 mb-16"
                                                id="shipping"
                                                label="Shipping"
                                                variant="outlined"
                                                fullWidth
                                                size="small"
                                                type="number"
                                                onKeyDown={preventNegativeValues}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="start">$</InputAdornment>
                                                    ),
                                                    readOnly: isReadOnly,

                                                }}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                        )}
                        {/* Fifth Row */}
                        
                        <div className="flex flex-col md:flex-row w-full gap-16">
                                {/* Custom Product Section - Margin */}
                                {!props.hideAdditionalFields && <div className="flex w-full md:w-1/4">
                                    <Controller
                                        name="margin"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                onKeyDown={preventNegativeValues}
                                                onChange={(event) => {
                                                    handleMarginChange(event, field)
                                                }}
                                                className="mt-8 mb-16"
                                                id="margin"
                                                label="Margin"
                                                variant="outlined"
                                                fullWidth
                                                size="small"
                                                type="number"
                                                InputProps={{
                                                    maxLength: 2,
                                                    endAdornment: (
                                                        <InputAdornment position="start">%</InputAdornment>
                                                    ),
                                                    readOnly: isReadOnly,

                                                }}
                                                required={(props.isManage && !isReadOnly)}
                                                error={!!errors.margin}
                                                helperText={errors?.margin?.message}
                                            />
                                        )}
                                    />
                                </div>}
                                {/* Custom Product Section - Discount */}
                                {showDiscount === true &&
                                    <div className='flex w-full md:w-1/4'>
                                        <Controller
                                            name="discount"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    className="mt-8 mb-16"
                                                    id="discount"
                                                    label="Discount"
                                                    variant="outlined"
                                                    fullWidth
                                                    size="small"
                                                    type="number"
                                                    onKeyDown={preventNegativeValues}
                                                    onChange={(event) => handlePercentageValue(event, field)}
                                                    InputLabelProps={{ shrink: true }}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="start">%</InputAdornment>
                                                        ),
                                                        readOnly: originalIsReadOnly
                                                       
                                                    }}
                                                />
                                            )}
                                        />
                                    </div>
                                }
                                {/* Custom Product Section - Unit Price */}
                                {!props.hideAdditionalFields && <div className="flex w-full md:w-1/4">
                                    <Controller
                                        name="unit_price"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                onKeyDown={preventNegativeValues}
                                                onChange={(event) => {
                                                    field.onChange(event.target.value);
                                                    handleUnitPriceChange(event, field)
                                                }}
                                                className="mt-8 mb-16"
                                                id="unit_price"
                                                label="Unit Price"
                                                variant="outlined"
                                                fullWidth
                                                size="small"
                                                type="number"
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="start">$</InputAdornment>
                                                    ),
                                                    readOnly: isReadOnly,
                                                }}
                                                required={(props.isManage && !isReadOnly)}
                                                error={!!errors.unit_price}
                                                helperText={errors?.unit_price?.message}
                                            />
                                        )}
                                    />
                                </div>}
                                <div className="flex w-full md:w-1/4" />
                        </div>
                        
                    </div>
                </div>
                {/* <div className="flex justify-center md:w-1/5">
                    {!isReadOnly && (
                        <Controller
                            name="stone_image"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <label
                                    htmlFor="button-file"
                                    className="productImageUpload flex items-center justify-center relative w-128 h-128 rounded-16 mx-12 mb-12 overflow-hidden cursor-pointer shadow hover:shadow-lg"
                                >
                                    <input
                                        accept="image/*"
                                        className="hidden"
                                        id="button-file"
                                        type="file"
                                        onChange={async (e) => {
                                            function readColorFileAsync() {
                                                return new Promise((resolve, reject) => {
                                                    const file = e.target.files[0];
                                                    if (!file) {
                                                        return;
                                                    }
                                                    dispatch(uploadFile({ file, addTo: 'Product' })).then((response) => {
                                                        const reader = new FileReader();
                                                        reader.onload = () => {
                                                            resolve({
                                                                id: response.payload.id,
                                                                content_type: file.type,
                                                                data: btoa(reader.result),
                                                            });
                                                        };
                                                        reader.onerror = reject;
                                                        reader.readAsBinaryString(file);
                                                    });
                                                });
                                            }
                                            const newImage = await readColorFileAsync();
                                            onChange({ ...newImage, ...value });
                                            const tempDta = {
                                                content_type: newImage.content_type,
                                                data: newImage.data,
                                                id: newImage.id,
                                            };
                                            const stoneColorData = { ...stoneColor };
                                            stoneColorData.attachment = tempDta;
                                            setValue('stone_color', stoneColorData);
                                        }}
                                    />
                                    {stoneColor && stoneColor.attachment && (
                                        <div className="productImageItem flex items-center justify-center relative w-128 h-128  featured rounded-16 overflow-hidden cursor-pointer outline-none ">
                                            <img
                                                src={resolveFileSrc(stoneColor.attachment)}
                                                className="max-w-none w-auto h-full"

                                                alt=''
                                            />
                                        </div>
                                    )}
                                    {(!stoneColor || (stoneColor && !stoneColor.attachment)) && (
                                        <Icon fontSize="large" color="action">
                                            cloud_upload
                                        </Icon>
                                    )}
                                    {stoneColor && stoneColor.attachment && (
                                        <IconButton
                                            className="productImageFeaturedStar absolute top-0 right-0"
                                            onClick={(ev) => {
                                                clearImage(ev)
                                            }}
                                            size="small"
                                        >
                                            <Icon>clear</Icon>
                                        </IconButton>
                                    )}
                                </label>
                            )}
                        />
                    )}
                    {isReadOnly && stoneColor && stoneColor.attachment && (
                        <div className="productImageItem flex items-center justify-center relative w-128 h-128  featured rounded-16 overflow-hidden cursor-pointer outline-none ">
                            <img
                                src={resolveFileSrc(stoneColor.attachment)}
                                className="max-w-none w-auto h-full"
                                alt=""
                            />
                        </div>
                    )}
                </div> */}

            </div>

            <div className="flex flex-col md:flex-row w-full">
                <Controller
                    name="comments"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            className="mt-8 mb-16"
                            id="note"
                            label="Comments"
                            type="text"
                            multiline
                            rows={5}
                            variant="outlined"
                            fullWidth
                            size="small"
                            InputProps={{
                                readOnly: originalIsReadOnly,
                            }}
                        />
                    )}
                />
            </div>

        </div>
    );
}

export default CustomProductFormContent;